import React, { useCallback, useLayoutEffect, useState } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import { AppView, CardView, Spacer, withAnimatedNavigationHeader } from 'design-system';
import Links from 'utils/links';
import * as actions from 'actions';
import { useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { useAppDispatch } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { OnboardingFlow } from 'utils/types/navigation';
import FormattedAddress from 'features/invest/components/FormattedAddress';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
const ConfirmAddressScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route: { params } }) => {
    const commonStyles = useCommonInvestStyles();
    const colors = useColors();
    const dispatch = useAppDispatch();
    const [, , loading, fetch] = useFetchOld({ throws: true });
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const [checked, setChecked] = useState(false);
    const onCheckPress = useCallback(() => {
        setChecked((oldValue) => !oldValue);
    }, []);
    const flow = params?.flow;
    const postData = params?.postData;
    const skipPostAddress = params?.skipPostAddress;
    const addressInDatabase = params?.addressInDatabase;
    const shouldUpdateAddress = params?.shouldUpdateAddress;
    const { county, buildingName, buildingNumber, subBuildingName, townOrCity: town, postcode: postCode, streetName: street, } = postData;
    useAmplitudeOnMountEvent(flow, 'User Address Confirmation Screen');
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenForPots = useNavigateToNextScreenForPotsOnboarding();
    const onDone = useCallback(async () => {
        try {
            let response;
            if (!skipPostAddress) {
                if (shouldUpdateAddress && addressInDatabase) {
                    response = await fetch(actions.updateUserAddress(addressInDatabase.id, postData));
                }
                else {
                    response = await fetch(actions.postUserAddress(postData));
                }
            }
            if (skipPostAddress || response) {
                dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestInsurance', params: {} });
                if (flow === OnboardingFlow.Pots || flow === OnboardingFlow.Wallet) {
                    navigateToNextScreenForPots();
                }
                else {
                    navigateToNextScreen();
                }
            }
            else {
                // middleware shows error message (TODO: show validation error in that message)
            }
        }
        catch {
            // middleware will show error alert for us
        }
    }, [
        skipPostAddress,
        shouldUpdateAddress,
        addressInDatabase,
        postData,
        flow,
        navigateToNextScreenForPots,
        navigateToNextScreen,
    ]);
    const openRequirements = useCallback(() => {
        Links.openKycProcessLink();
    }, []);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, commonStyles.contentContainer); // 56 is Button’s height, 20 is margin to the top of Button, 16 is the space between buttons
    return (<AppView withFrame={false}>
        <View style={commonStyles.containerNoPaddingTransparent}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom}>
            <ScreenInfoHeader title={onboardingTitleStrings.ConfirmAddress.title} subTitle={onboardingTitleStrings.ConfirmAddress.subtitle}/>
            <CardView withPadding noMarginTop>
              <FormattedAddress buildingName={buildingName} buildingNumber={buildingNumber} subBuildingName={subBuildingName} streetName={street} townOrCity={town} county={county} postcode={postCode} uk/>
            </CardView>
            <TouchableBounce onPress={onCheckPress}>
              <CardView withPadding noMarginTop>
                <View style={styles.row}>
                  <RadioButton type="multiple" isChecked={checked}/>
                  <Spacer.W16 />
                  <View style={styles.flex}>
                    <Text TextThin-14>I confirm that my address is correct.</Text>
                  </View>
                </View>
              </CardView>
            </TouchableBounce>
            <View style={styles.horizontalPadding}>
              <Text TextThin-14 Secondary>
                If the address provided does not match your legal address, your application will be rejected.{' '}
                <Text Text-14 Primary onPress={openRequirements} suppressHighlighting>
                  Learn more
                </Text>
              </Text>
            </View>
          </Animated.ScrollView>
          <Button floating brand title="Continue" onPress={onDone} isFetching={loading} disabled={!checked}/>
        </View>
      </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: onboardingTitleStrings.ConfirmAddress.shortTitle,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    citizenshipInfo: {
        maxWidth: '60%',
        textAlign: 'right',
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
});
export default ConfirmAddressScreen;
